import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { MiddleCategoryTable, MiddleCategoryFilter, Pagination, MiddleCategoryFormModal } from '../components';
import { fetchMiddleCategories, createMiddleCategory, createBulkMiddleCategories } from '../services/api';

const MiddleCategoryManagement = () => {
  const [middleCategories, setMiddleCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [filters, setFilters] = useState({
    categoryName: 'Arts'
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  const fetchMiddleCategoriesData = async (categoryName = filters.categoryName) => {
    setLoading(true);
    try {
      const data = await fetchMiddleCategories(categoryName);
      setMiddleCategories(data || []);
      
      // Since the API returns an array directly, we don't have pagination info
      // Set totalPages to 1 as it's a simple array response
      setTotalPages(1);
      setCurrentPage(0);
    } catch (error) {
      console.error('Error fetching middle categories:', error);
      toast.error('Failed to fetch middle categories. Please try again.');
      setMiddleCategories([]);
      setTotalPages(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMiddleCategoriesData();
  }, [filters]);

  const handlePageChange = (newPage) => {
    if (newPage !== currentPage) {
      setCurrentPage(newPage);
      // Since we don't have actual pagination from API, this is just for UI consistency
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(0);
  };

  const handleCreateMiddleCategory = async (middleCategoryData) => {
    setLoading(true);
    try {
      await createMiddleCategory(middleCategoryData);
      toast.success('Middle category created successfully!');
      setShowCreateModal(false);
      fetchMiddleCategoriesData(); // Refresh data
    } catch (error) {
      console.error('Error creating middle category:', error);
      toast.error('Failed to create middle category. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBulkCreateMiddleCategories = async (bulkData) => {
    setLoading(true);
    try {
      await createBulkMiddleCategories(bulkData);
      const count = bulkData.middleCategories.length;
      toast.success(`${count} middle categories created successfully!`);
      setShowCreateModal(false);
      fetchMiddleCategoriesData(); // Refresh data
    } catch (error) {
      console.error('Error creating bulk middle categories:', error);
      toast.error('Failed to create middle categories. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Middle Category Management</h1>
        <button
          onClick={() => setShowCreateModal(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Add Middle Category
        </button>
      </div>

      <MiddleCategoryFilter onFilterChange={handleFilterChange} initialFilters={filters} />

      <MiddleCategoryTable 
        middleCategories={middleCategories} 
        loading={loading}
        onRefresh={fetchMiddleCategoriesData}
      />

      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}

      {showCreateModal && (
        <MiddleCategoryFormModal
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreateMiddleCategory}
          onBulkSave={handleBulkCreateMiddleCategories}
        />
      )}
    </div>
  );
};

export default MiddleCategoryManagement;
